import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  RefreshCw, Plus, Search, Pencil, Trash2, Gem, Package, Loader2, CalendarIcon, X, CheckSquare2, Square,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { diamondsService } from '@/services/diamonds.service';
import NovoItemDialog from '@/components/NovoItemDialog';

const IMAGE_BASE_URL = 'https://xdoinfinitto.com.br:3000';

interface ItemListagem {
  id: number;
  nome: string;
  descricao?: string;
  imagem: string;
  valor: number;
  custo: number;
  qntd: number;
  qntd_disponivel: number;
  qntd_resgatada: number;
  ativo: boolean;
  categoria_id: number | null;
  categoria_nome: string | null;
  date_created: string;
  date_updated: string | null;
}

const formatDate = (value: string): string => {
  try {
    if (!value) return '-';
    return format(new Date(value), 'dd/MM HH:mm', { locale: ptBR });
  } catch {
    return '-';
  }
};

const formatBRL = (value: number): string =>
  (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function GerenciamentoItens() {
  const navigate = useNavigate();

  const [items, setItems] = useState<ItemListagem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [ativoFilter, setAtivoFilter] = useState<boolean | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [statusDropdown, setStatusDropdown] = useState<string>('all');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ItemListagem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  // Seleção em massa
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkLoading, setIsBulkLoading] = useState(false);

  const loadItems = useCallback(async (
    page: number,
    size: number,
    search: string,
    ativo?: boolean,
    dateStart?: Date,
    dateEnd?: Date,
  ) => {
    setIsLoading(true);
    try {
      const result = await diamondsService.getItensPaginados(
        page, size, search, ativo,
        dateStart ? format(dateStart, 'yyyy-MM-dd') : undefined,
        dateEnd ? format(dateEnd, 'yyyy-MM-dd') : undefined,
      );
      setItems((result.data ?? []) as ItemListagem[]);
      setTotalItems(result.pagination?.total ?? 0);
      setTotalPages(result.pagination?.totalPages ?? 1);
    } catch (error) {
      console.error('Erro ao buscar itens:', error);
      toast.error('Erro ao buscar itens');
      setItems([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems(1, pageSize, '', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchFilter(value);
      setCurrentPage(1);
      loadItems(1, pageSize, value, ativoFilter, startDate, endDate);
    }, 350);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusDropdown(value);
    const newAtivo = value === 'true' ? true : value === 'false' ? false : undefined;
    setAtivoFilter(newAtivo);
    setCurrentPage(1);
    loadItems(1, pageSize, searchFilter, newAtivo, startDate, endDate);
  };

  const handleStatusChange = async (item: ItemListagem, novoValor: string) => {
    const novoAtivo = novoValor === 'true';
    if (novoAtivo === item.ativo) return;
    try {
      await diamondsService.updateAtivoItem(item.id, novoAtivo);
      toast.success('Status atualizado!');
      await loadItems(currentPage, pageSize, searchFilter, ativoFilter, startDate, endDate);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error(error?.message || 'Erro ao atualizar status do item');
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await diamondsService.deleteItem(itemToDelete.id);
      toast.success('Item deletado com sucesso!');
      setItemToDelete(null);
      // se a página ficou vazia após deletar, volta para a anterior
      const newPage = items.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      setCurrentPage(newPage);
      await loadItems(newPage, pageSize, searchFilter, ativoFilter, startDate, endDate);
    } catch (error) {
      console.error('Erro ao deletar item:', error);
      toast.error('Erro ao deletar item');
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    loadItems(newPage, pageSize, searchFilter, ativoFilter, startDate, endDate);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    loadItems(1, newSize, searchFilter, ativoFilter, startDate, endDate);
  };

  const aplicarFiltroData = () => {
    if (!startDate || !endDate) {
      toast.error('Selecione as datas de início e fim');
      return;
    }
    setCurrentPage(1);
    loadItems(1, pageSize, searchFilter, ativoFilter, startDate, endDate);
  };

  // Seleção em massa
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const isAllPageSelected = items.length > 0 && items.every(i => selectedIds.has(i.id));

  const toggleSelectAllPage = () => {
    if (isAllPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        items.forEach(i => next.delete(i.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        items.forEach(i => next.add(i.id));
        return next;
      });
    }
  };

  const handleBulkStatusChange = async (ativo: boolean) => {
    if (selectedIds.size === 0) return;
    try {
      setIsBulkLoading(true);
      const resp = await diamondsService.updateAtivoItemBulk(Array.from(selectedIds), ativo);
      if (resp?.semEstoque?.length > 0) {
        toast.warning(`${resp.message}`);
      } else {
        toast.success(resp?.message ?? 'Itens atualizados com sucesso');
      }
      setSelectedIds(new Set());
      await loadItems(currentPage, pageSize, searchFilter, ativoFilter, startDate, endDate);
    } catch (error: any) {
      console.error('Erro ao atualizar itens em massa:', error);
      toast.error(error?.message || 'Erro ao atualizar itens em massa');
    } finally {
      setIsBulkLoading(false);
    }
  };

  const limparFiltros = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setSearchInput('');
    setSearchFilter('');
    setStatusDropdown('all');
    setAtivoFilter(undefined);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setCurrentPage(1);
    setSelectedIds(new Set());
    loadItems(1, pageSize, '', undefined, undefined, undefined);
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <div>
              <CardTitle>Gerencie os benefícios de resgate de diamantes</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Total: {totalItems} {totalItems === 1 ? 'item' : 'itens'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadItems(currentPage, pageSize, searchFilter, ativoFilter, startDate, endDate)}
                disabled={isLoading}
              >
                <RefreshCw className={cn('h-4 w-4 mr-2', isLoading && 'animate-spin')} />
                Atualizar
              </Button>
              <Button size="sm" onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Novo Item
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Filtros */}
          <div className="flex flex-col gap-3 mb-6">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome, categoria ou descrição..."
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusDropdown} onValueChange={handleStatusFilterChange}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="true">Ativo</SelectItem>
                  <SelectItem value="false">Inativo</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => handlePageSizeChange(Number(v))}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 por pág.</SelectItem>
                  <SelectItem value="25">25 por pág.</SelectItem>
                  <SelectItem value="50">50 por pág.</SelectItem>
                  <SelectItem value="100">100 por pág.</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filtro por período */}
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs font-medium mb-1 block text-muted-foreground">Data início</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !startDate && 'text-muted-foreground',
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'dd/MM/yyyy', { locale: ptBR }) : 'Selecionar data'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs font-medium mb-1 block text-muted-foreground">Data fim</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !endDate && 'text-muted-foreground',
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'dd/MM/yyyy', { locale: ptBR }) : 'Selecionar data'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex gap-2">
                <Button onClick={aplicarFiltroData} disabled={!startDate || !endDate} size="sm">
                  Aplicar período
                </Button>
                <Button onClick={limparFiltros} variant="outline" size="sm">
                  <X className="h-4 w-4 mr-1" />
                  Limpar
                </Button>
              </div>
            </div>
          </div>

          {/* Barra de ações em massa */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-muted/60 rounded-lg border">
              <span className="text-sm font-medium">{selectedIds.size} item(ns) selecionado(s)</span>
              <div className="flex gap-2 ml-auto">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-green-700 border-green-300 hover:bg-green-50"
                  disabled={isBulkLoading}
                  onClick={() => handleBulkStatusChange(true)}
                >
                  {isBulkLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Ativar selecionados
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-700 border-red-300 hover:bg-red-50"
                  disabled={isBulkLoading}
                  onClick={() => handleBulkStatusChange(false)}
                >
                  {isBulkLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Inativar selecionados
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={isBulkLoading}
                  onClick={() => setSelectedIds(new Set())}
                >
                  Limpar seleção
                </Button>
              </div>
            </div>
          )}

          {/* Loading */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <Package className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhum item encontrado.</p>
            </div>
          ) : (
            <>
              {/* Tabela desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="p-3 w-10">
                        <button onClick={toggleSelectAllPage} className="flex items-center">
                          {isAllPageSelected
                            ? <CheckSquare2 className="h-5 w-5 text-primary" />
                            : <Square className="h-5 w-5 text-muted-foreground" />}
                        </button>
                      </th>
                      <th className="text-left p-3 font-semibold">Item</th>
                      <th className="text-left p-3 font-semibold">Categoria</th>
                      <th className="text-left p-3 font-semibold">Valor</th>
                      <th className="text-left p-3 font-semibold">Qntds</th>
                      <th className="text-left p-3 font-semibold">Criado em</th>
                      <th className="text-left p-3 font-semibold">Status</th>
                      <th className="text-right p-3 font-semibold">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} className={cn('border-b hover:bg-muted/50', selectedIds.has(item.id) && 'bg-muted/30')}>
                        <td className="p-3">
                          <button onClick={() => toggleSelect(item.id)} className="flex items-center">
                            {selectedIds.has(item.id)
                              ? <CheckSquare2 className="h-5 w-5 text-primary" />
                              : <Square className="h-5 w-5 text-muted-foreground" />}
                          </button>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={`${IMAGE_BASE_URL}/diamonds/item-image/${item.imagem}`}
                              alt={item.nome}
                              className="w-12 h-12 rounded object-cover bg-muted"
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                if (!img.src.endsWith('/placeholder.svg')) {
                                  img.src = '/placeholder.svg';
                                }
                              }}
                            />
                            <span className="font-medium">{item.nome}</span>
                          </div>
                        </td>
                        <td className="p-3">{item.categoria_nome || 'Sem categoria'}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <span className="text-purple-600 text-xl font-bold">{item.valor}</span>
                            <Gem className="h-4 w-4 text-purple-600" />
                          </div>
                          {/* <div className="text-xs text-muted-foreground">{formatBRL(item.custo)}</div> */}
                        </td>
                        <td className="p-3">
                          <div className="font-medium">{item.qntd_disponivel} <span className="text-xs text-muted-foreground">disponível</span></div>
                          <div className="text-xs text-muted-foreground">{item.qntd_resgatada} resgatadas</div>
                        </td>
                        <td className="p-3">{formatDate(item.date_created)}</td>
                        <td className="p-3">
                          <Select
                            value={String(item.ativo)}
                            onValueChange={(v) => handleStatusChange(item, v)}
                          >
                            <SelectTrigger className={cn(
                              'w-[110px] h-8',
                              item.ativo
                                ? 'bg-green-50 border-green-200 text-green-800'
                                : 'bg-red-50 border-red-200 text-red-800',
                            )}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="true">Ativo</SelectItem>
                              <SelectItem value="false">Inativo</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`/v2/gerenciamento-itens/${item.id}`, '_blank')}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setItemToDelete(item)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Cards mobile */}
              <div className="md:hidden space-y-3">
                {items.map((item) => (
                  <Card key={item.id} className="p-3">
                    <div className="flex gap-3">
                      <img
                        src={`${IMAGE_BASE_URL}/diamonds/item-image/${item.imagem}`}
                        alt={item.nome}
                        className="w-16 h-16 rounded object-cover bg-muted shrink-0"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          if (!img.src.endsWith('/placeholder.svg')) {
                            img.src = '/placeholder.svg';
                          }
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium truncate">{item.nome}</p>
                          <Badge className={item.ativo ? 'bg-green-500' : 'bg-red-500'}>
                            {item.ativo ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{item.categoria_nome || 'Sem categoria'}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <span className="text-purple-600 font-bold text-lg">{item.valor}</span>
                          <Gem className="h-4 w-4 text-purple-600" />
                          <span className="text-xs text-muted-foreground ml-2">{formatBRL(item.custo)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          <span className="text-foreground font-medium">{item.qntd_disponivel}</span> disponível · <span>{item.qntd_resgatada}</span> resgatadas — {formatDate(item.date_created)}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => window.open(`/v2/gerenciamento-itens/${item.id}`, '_blank')}
                          >
                            <Pencil className="h-3 w-3 mr-1" /> Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive"
                            onClick={() => setItemToDelete(item)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t gap-2">
                  <span className="text-sm text-muted-foreground">
                    Mostrando {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalItems)} de {totalItems}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1 || isLoading}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Anterior
                    </Button>
                    <span className="flex items-center text-sm px-2">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages || isLoading}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Próximo
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal criar */}
      <NovoItemDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreated={() => loadItems(1, pageSize, searchFilter, ativoFilter, startDate, endDate)}
      />

      {/* Confirmação de exclusão */}
      <AlertDialog open={!!itemToDelete} onOpenChange={(o) => !o && setItemToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir item</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja deletar o item <strong>{itemToDelete?.nome}</strong>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-destructive hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
